"use client";

import { useEffect, useRef, useState } from "react";

/**
 * VortexSymbol — símbolo Vorseum em vórtice dinâmico (VOR-64 + VOR-75 iter 2).
 *
 * Estado padrão (sem hover):
 *   • Os 3 círculos ficam *espalhados* radialmente (translate pra fora)
 *     e *menores* (scale ~0.85 cada) — não formam ainda o glifo.
 *   • Cada círculo rotaciona em torno do próprio centro com vorticidade
 *     proporcional à proximidade do eixo (Rankine — pequeno gira mais
 *     rápido).
 *   • SVG inteiro também rotaciona devagar — vórtice global.
 *
 * Estado hover:
 *   • Translate radial vai pra zero + scale volta a 1 + rotação acelera.
 *   • Os 3 círculos *convergem* e *formam* o glifo Vorseum original
 *     (Bea: "ao passar o ponteiro, eles se aproximam e formam o symbol").
 *
 * Cores: brancas. Sem lilás (VOR-75 iter 1).
 *
 * Mouse 2D faz tilt parallax (rotateX/Y até 14°). Respeita
 * `prefers-reduced-motion`: tudo estático sem tilt nem animação.
 */
export function VortexSymbol() {
  const ref = useRef<HTMLDivElement>(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [hovering, setHovering] = useState(false);
  const reducedRef = useRef(false);

  useEffect(() => {
    reducedRef.current = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
  }, []);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (reducedRef.current) return;
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const dx = (e.clientX - r.left) / r.width - 0.5;
    const dy = (e.clientY - r.top) / r.height - 0.5;
    setTilt({ x: -dy * 14, y: dx * 14 });
  };

  const handleMouseLeave = () => {
    setHovering(false);
    setTilt({ x: 0, y: 0 });
  };

  const speed = hovering ? 2.4 : 1;

  // Translate radial padrão pra "espalhar" cada anel a partir do
  // centro do glifo (16, 16). No hover vai pra (0, 0) — convergem.
  // Vetores normalizados das posições dos centros dos anéis ao centro
  // do SVG, escalados pra ~3 unidades (em coords SVG 32x32).
  const offsets = hovering
    ? { large: [0, 0], mid: [0, 0], small: [0, 0] }
    : {
        large: [-3.5, 0], // grande sai pra esquerda
        mid: [3.5, 2.2], // médio sai pra direita-baixo
        small: [0, 3.2], // pequeno sai pra baixo
      };

  // Scale padrão menor; hover volta a 1 (forma o glifo coeso).
  const ringScale = hovering ? 1 : 0.85;

  return (
    <div
      ref={ref}
      onMouseEnter={() => setHovering(true)}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      aria-hidden="true"
      className="relative mx-auto aspect-square w-full max-w-[560px] [perspective:1200px]"
    >
      <div
        className="relative h-full w-full transition-transform duration-300 ease-out will-change-transform"
        style={{
          transform: `rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
        }}
      >
        <svg
          viewBox="0 0 32 32"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="absolute inset-0 h-full w-full drop-shadow-[0_0_24px_rgba(255,255,255,0.12)]"
          style={{
            transformOrigin: "center",
            animation: `vortex-spin-cw ${30 / speed}s linear infinite`,
          }}
        >
          {/* Estrutura aninhada por anel:
            *   <g> externo: translate + scale (sem animation) — controla
            *     "espalhar" (default) vs "convergir" (hover) com transition.
            *   <g> interno: animation rotate em torno do centro do path —
            *     gira indefinidamente sem afetar a separação radial. */}

          {/* Anel grande — vorticidade baixa: 18s. Sai pra esquerda. */}
          <g
            style={{
              translate: `${offsets.large[0]}px ${offsets.large[1]}px`,
              scale: ringScale,
              transformOrigin: "14.0882px 15.9999px",
              transition:
                "translate 0.6s cubic-bezier(0.34, 1.56, 0.64, 1), scale 0.6s ease-out",
            }}
          >
            <g
              style={{
                transformOrigin: "14.0882px 15.9999px",
                animation: `vortex-pulse-large ${18 / speed}s linear infinite`,
              }}
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M14.0882 25.5562C19.3674 25.5562 23.6471 21.2777 23.6471 15.9999C23.6471 10.7222 19.3674 6.44368 14.0882 6.44368C8.80904 6.44368 4.52941 10.7222 4.52941 15.9999C4.52941 21.2777 8.80904 25.5562 14.0882 25.5562ZM14.0882 27.0852C20.2121 27.0852 25.1765 22.1222 25.1765 15.9999C25.1765 9.87772 20.2121 4.91467 14.0882 4.91467C7.96437 4.91467 3 9.87772 3 15.9999C3 22.1222 7.96437 27.0852 14.0882 27.0852Z"
                fill="#FFFFFF"
                fillOpacity="0.95"
              />
            </g>
          </g>

          {/* Anel médio — vorticidade média: 12s reverso. Sai pra direita-baixo. */}
          <g
            style={{
              translate: `${offsets.mid[0]}px ${offsets.mid[1]}px`,
              scale: ringScale,
              transformOrigin: "20.9706px 19.058px",
              transition:
                "translate 0.6s cubic-bezier(0.34, 1.56, 0.64, 1), scale 0.6s ease-out",
            }}
          >
            <g
              style={{
                transformOrigin: "20.9706px 19.058px",
                animation: `vortex-orbit-mid ${12 / speed}s linear infinite reverse`,
              }}
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M20.9706 25.5562C24.5604 25.5562 27.4706 22.6468 27.4706 19.058C27.4706 15.4691 24.5604 12.5597 20.9706 12.5597C17.3807 12.5597 14.4706 15.4691 14.4706 19.058C14.4706 22.6468 17.3807 25.5562 20.9706 25.5562ZM20.9706 27.0852C25.4051 27.0852 29 23.4913 29 19.058C29 14.6246 25.4051 11.0307 20.9706 11.0307C16.5361 11.0307 12.9412 14.6246 12.9412 19.058C12.9412 23.4913 16.5361 27.0852 20.9706 27.0852Z"
                fill="#FFFFFF"
                fillOpacity="0.85"
              />
            </g>
          </g>

          {/* Anel pequeno — vorticidade alta: 6s. Sai pra baixo. */}
          <g
            style={{
              translate: `${offsets.small[0]}px ${offsets.small[1]}px`,
              scale: ringScale,
              transformOrigin: "16px 20.2047px",
              transition:
                "translate 0.6s cubic-bezier(0.34, 1.56, 0.64, 1), scale 0.6s ease-out",
            }}
          >
            <g
              style={{
                transformOrigin: "16px 20.2047px",
                animation: `vortex-orbit-small ${6 / speed}s linear infinite`,
              }}
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M16 25.5562C18.9563 25.5562 21.3529 23.1603 21.3529 20.2047C21.3529 17.2491 18.9563 14.8532 16 14.8532C13.0437 14.8532 10.6471 17.2491 10.6471 20.2047C10.6471 23.1603 13.0437 25.5562 16 25.5562ZM16 27.0852C19.801 27.0852 22.8824 24.0047 22.8824 20.2047C22.8824 16.4047 19.801 13.3242 16 13.3242C12.199 13.3242 9.11765 16.4047 9.11765 20.2047C9.11765 24.0047 12.199 27.0852 16 27.0852Z"
                fill="#FFFFFF"
                fillOpacity="1"
              />
            </g>
          </g>
        </svg>
      </div>
    </div>
  );
}
